import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { buscarCabecalhoEmpresa, desenharCabecalhoPadrao, desenharRodapePaginacao } from "./relatorioCabecalho";

// Primeiro relatório do sistema que não é tabular — cards, caixas de
// alerta coloridas por severidade, texto corrido. Por isso não usa
// `exportarRelatorioPDF` (utils/relatorioPdf.js), que só desenha tabela;
// reaproveita dela só o cabeçalho/rodapé padrão (relatorioCabecalho.js).

const COR_TEXTO = [40, 40, 40];
const COR_TEXTO_FRACO = [130, 130, 130];
const COR_VERDE = [26, 61, 42];

const COR_SEVERIDADE = {
  inconsistencia: { fundo: [254, 242, 242], borda: [252, 165, 165], texto: [153, 27, 27], rotulo: "Inconsistência" },
  dados_ausentes: { fundo: [255, 251, 235], borda: [252, 211, 77], texto: [146, 64, 14], rotulo: "Dados ausentes" },
  aviso: { fundo: [239, 246, 255], borda: [147, 197, 253], texto: [30, 64, 175], rotulo: "Aviso" },
};

function formatarNumero(valor, casas = 2) {
  if (valor === null || valor === undefined) return "—";
  return Number(valor).toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas });
}

function formatarMoeda(valor) {
  if (valor === null || valor === undefined) return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valor);
}

/** Título de seção — barra fina colorida + texto, mesmo estilo em toda seção do relatório. */
function desenharTituloSecao(doc, texto, x, y, largura) {
  doc.setFillColor(...COR_VERDE);
  doc.rect(x, y, 3, 5, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COR_TEXTO);
  doc.text(texto, x + 6, y + 4.2);
  doc.setDrawColor(230);
  doc.line(x, y + 8, x + largura, y + 8);
  return y + 13;
}

/** Lista de pares rótulo/valor em duas colunas, como uma tabela sem bordas. */
function desenharChaveValor(doc, pares, x, y, largura, colunas = 2) {
  const larguraColuna = largura / colunas;
  let coluna = 0;
  let linhaY = y;
  const alturaLinha = 5.5;

  pares.forEach(([rotulo, valor]) => {
    const cx = x + coluna * larguraColuna;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COR_TEXTO_FRACO);
    doc.text(rotulo, cx, linhaY);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...COR_TEXTO);
    doc.text(String(valor), cx, linhaY + 4.2);

    coluna += 1;
    if (coluna >= colunas) {
      coluna = 0;
      linhaY += alturaLinha + 4;
    }
  });

  if (coluna !== 0) linhaY += alturaLinha + 4;
  return linhaY;
}

/** Card com título + valor grande + legenda, usado nas 3 recomendações principais. */
function desenharCard(doc, { titulo, valor, legenda }, x, y, largura, altura) {
  doc.setDrawColor(220);
  doc.setFillColor(250, 250, 250);
  doc.roundedRect(x, y, largura, altura, 2, 2, "FD");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COR_TEXTO_FRACO);
  doc.text(titulo.toUpperCase(), x + 4, y + 6);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...COR_TEXTO);
  const linhasValor = doc.splitTextToSize(valor, largura - 8);
  doc.text(linhasValor, x + 4, y + 13);

  if (legenda) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...COR_TEXTO_FRACO);
    const linhasLegenda = doc.splitTextToSize(legenda, largura - 8);
    doc.text(linhasLegenda, x + 4, y + 13 + linhasValor.length * 5 + 3);
  }
}

/** Caixa de alerta colorida por severidade, com quebra de linha automática. */
function desenharAlerta(doc, alerta, x, y, largura) {
  const cor = COR_SEVERIDADE[alerta.severidade] || COR_SEVERIDADE.aviso;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const texto = `${cor.rotulo}: ${alerta.mensagem}`;
  const linhas = doc.splitTextToSize(texto, largura - 8);
  const altura = linhas.length * 4.2 + 5;

  doc.setFillColor(...cor.fundo);
  doc.setDrawColor(...cor.borda);
  doc.roundedRect(x, y, largura, altura, 1.5, 1.5, "FD");
  doc.setTextColor(...cor.texto);
  doc.text(linhas, x + 4, y + 5.5);

  return y + altura + 4;
}

export async function exportarRelatorioAnaliseSoloPDF(dados) {
  const { identificacao, parametros, saturacao_elementos: satEl, relacoes, micronutrientes, calagem, potassio, fosforo, alertas, custo_estimado: custo } = dados;

  const empresa = await buscarCabecalhoEmpresa();
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const largura = doc.internal.pageSize.getWidth();
  const altura = doc.internal.pageSize.getHeight();
  const margem = 12;
  const larguraUtil = largura - margem * 2;

  const alturaCabecalho = 34;
  desenharCabecalhoPadrao(doc, {
    titulo: "Recomendação de Calagem e Adubação",
    empresa,
    largura,
    margem,
  });

  let y = margem + alturaCabecalho + 8;
  const garantirEspaco = (necessario) => {
    if (y + necessario > altura - 16) {
      doc.addPage();
      desenharCabecalhoPadrao(doc, { titulo: "Recomendação de Calagem e Adubação", empresa, largura, margem });
      y = margem + alturaCabecalho + 8;
    }
  };

  // Identificação
  y = desenharTituloSecao(doc, "Identificação", margem, y, larguraUtil);
  y = desenharChaveValor(doc, [
    ["Propriedade", identificacao.propriedade || "Não informada"],
    ["Talhão", identificacao.talhao],
    ["Área", identificacao.area_ha ? `${formatarNumero(identificacao.area_ha)} ha` : "Não informada"],
    ["Laboratório", identificacao.laboratorio || "Não informado"],
    ["Certificado", identificacao.certificado || "Não informado"],
    ["Data da coleta", identificacao.data_coleta || "Não informada"],
    ["Camada analisada", identificacao.camada_cm],
    ["Cultura/finalidade", identificacao.cultura_finalidade || "Não informada"],
  ], margem, y, larguraUtil, 3);
  y += 4;

  // Recomendação — 3 cards lado a lado
  garantirEspaco(30);
  y = desenharTituloSecao(doc, "Recomendação", margem, y, larguraUtil);
  const larguraCard = (larguraUtil - 8) / 3;
  desenharCard(doc, {
    titulo: "Calagem",
    valor: calagem.dispensada ? "Dispensada" : `${formatarNumero(calagem.dose_recomendada_t_ha, 1)} t/ha`,
    legenda: calagem.dispensada ? "Solo dentro da meta de saturação." : "Neutralização de alumínio, PRNT 100%.",
  }, margem, y, larguraCard, 26);
  desenharCard(doc, {
    titulo: "Adubação Fosfatada",
    valor: fosforo.dose_p2o5_kg_ha != null ? `${formatarNumero(fosforo.dose_p2o5_kg_ha, 0)} kg/ha P₂O₅` : "Sem tabela",
    legenda: fosforo.classe_teor ? `Classe: ${fosforo.classe_teor.replace("_", " ")}` : null,
  }, margem + larguraCard + 4, y, larguraCard, 26);
  desenharCard(doc, {
    titulo: "Adubação Potássica",
    valor: potassio.dose_k2o_kg_ha != null ? (potassio.necessaria ? `${formatarNumero(potassio.dose_k2o_kg_ha, 0)} kg/ha K₂O` : "Não necessária") : "Sem K",
    legenda: potassio.k_mg_dm3 != null ? `K = ${formatarNumero(potassio.k_mg_dm3, 1)} mg/dm³` : null,
  }, margem + (larguraCard + 4) * 2, y, larguraCard, 26);
  y += 26 + 6;

  // Alertas
  if (alertas.length > 0) {
    garantirEspaco(20);
    y = desenharTituloSecao(doc, "Alertas e Validações", margem, y, larguraUtil);
    alertas.forEach((a) => {
      garantirEspaco(14);
      y = desenharAlerta(doc, a, margem, y, larguraUtil);
    });
    y += 2;
  }

  // Parâmetros
  garantirEspaco(60);
  y = desenharTituloSecao(doc, "Parâmetros", margem, y, larguraUtil);
  y = desenharChaveValor(doc, [
    ["pH", formatarNumero(parametros.ph)],
    ["Ca (cmolc/dm³)", formatarNumero(parametros.ca)],
    ["Mg (cmolc/dm³)", formatarNumero(parametros.mg)],
    ["K (cmolc/dm³)", formatarNumero(parametros.k_cmolc, 3)],
    ["K (mg/dm³)", formatarNumero(parametros.k_mg_dm3, 1)],
    ["Al (cmolc/dm³)", formatarNumero(parametros.al)],
    ["H+Al (cmolc/dm³)", formatarNumero(parametros.h_mais_al)],
    ["P (mg/dm³)", formatarNumero(parametros.p)],
    ["Matéria orgânica (dag/kg)", formatarNumero(parametros.materia_organica)],
    ["Argila (%)", formatarNumero(parametros.argila_pct)],
    ["Soma de Bases (SB)", formatarNumero(parametros.sb)],
    ["CTC efetiva (t)", formatarNumero(parametros.t_efetiva)],
    ["CTC total (T)", formatarNumero(parametros.t_total)],
    ["V% — Saturação por bases", formatarNumero(parametros.v_pct, 1)],
    ["m% — Saturação por alumínio", formatarNumero(parametros.m_pct, 1)],
  ], margem, y, larguraUtil, 3);
  y += 4;

  garantirEspaco(30);
  y = desenharTituloSecao(doc, "Saturação por Elemento e Relações entre Bases", margem, y, larguraUtil);
  y = desenharChaveValor(doc, [
    ["Sat. Cálcio (%)", formatarNumero(satEl.ca, 1)],
    ["Sat. Magnésio (%)", formatarNumero(satEl.mg, 1)],
    ["Sat. Potássio (%)", formatarNumero(satEl.k, 2)],
    ["Ca / Mg", formatarNumero(relacoes.ca_mg)],
    ["Ca / K", formatarNumero(relacoes.ca_k, 1)],
    ["Mg / K", formatarNumero(relacoes.mg_k, 1)],
  ], margem, y, larguraUtil, 3);
  y += 4;

  garantirEspaco(20);
  y = desenharTituloSecao(doc, "Micronutrientes", margem, y, larguraUtil);
  y = desenharChaveValor(doc, [
    ["S (mg/dm³)", formatarNumero(micronutrientes.s)],
    ["Zn (mg/dm³)", formatarNumero(micronutrientes.zn)],
    ["Cu (mg/dm³)", formatarNumero(micronutrientes.cu)],
    ["B (mg/dm³)", formatarNumero(micronutrientes.b)],
    ["Mn (mg/dm³)", formatarNumero(micronutrientes.mn)],
    ["Fe (mg/dm³)", formatarNumero(micronutrientes.fe)],
  ], margem, y, larguraUtil, 3);
  y += 4;

  // Custo estimado — única tabela do relatório, cabe bem num autoTable simples
  garantirEspaco(35);
  y = desenharTituloSecao(doc, "Custo Estimado", margem, y, larguraUtil);
  const linhasCusto = [
    ["Calcário", custo.calcario],
    ["Fósforo (P₂O₅)", custo.fosforo],
    ["Potássio (K₂O)", custo.potassio],
  ];
  autoTable(doc, {
    startY: y,
    margin: { left: margem, right: margem },
    head: [["Insumo", "Dose", "R$/ha", "Total no talhão"]],
    body: linhasCusto.map(([rotulo, linha]) => [
      rotulo,
      linha.dose != null ? `${formatarNumero(linha.dose, linha.unidade === "t/ha" ? 1 : 0)} ${linha.unidade}` : "—",
      formatarMoeda(linha.custo_ha),
      formatarMoeda(linha.custo_total),
    ]),
    theme: "grid",
    styles: { font: "helvetica", fontSize: 8.5, cellPadding: 2.5, textColor: COR_TEXTO },
    headStyles: { fillColor: COR_VERDE, textColor: 255, fontStyle: "bold" },
  });
  y = doc.lastAutoTable.finalY + 6;

  garantirEspaco(10);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7.5);
  doc.setTextColor(...COR_TEXTO_FRACO);
  doc.text(
    "Este documento não substitui a visita e a assinatura de engenheiro agrônomo. Siga sempre as instruções da bula dos produtos utilizados.",
    margem, y, { maxWidth: larguraUtil },
  );

  desenharRodapePaginacao(doc, { largura, altura, margem });
  window.open(doc.output("bloburl"), "_blank");
}
